import { computed, reactive, readonly, ref, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n'
import { useLocalStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { useAuthStore } from '.'
import { useParams } from '@/composables/useParams'
import { safeCopy, toast, mergeDeep, safeJson, isObject, ObjKeysLcFirst } from '@/common/util'
import webStorage from '@/common/webStorage'
import parametrosService, { paramsService } from '@/services/parametros.services'
import { useProgress } from '@/composables/useProgress'
import { config as configModel } from './model/config'

import type { LoaderType, ViewMediaType } from '@/types/types'
import { termsService } from '@/services/termosDeUso.service'

export interface ISysOptions {
  mode: number
  dark: number
  fullWidth: number
  volume: number
}

export const useSystemStore = defineStore('system', () => {
  const i18n = useI18n()
  const { batchMode } = useParams()

  const logoFiles = {
    icon: null as File | null,
    menu: null as File | null,
    loginLight: null as File | null,
    loginDark: null as File | null
  }

  const clientSettings = useLocalStorage<any>('clientSettings', {
    status: 'initial' as LoaderType,
    loadedOnce: false,
    settings: safeCopy(configModel) as typeof configModel,
    settingsBackgroundBanner: [] as string[],
    timestamp: null,
    lang: 'pt',
    aad: { // Azure Active Directory
      enabled: false,
      provider: '',
      origin: '',
    }
  }, {
    mergeDefaults: true
  })

  const tenantParameters = ref('')

  const langs = computed(() => ([
    {
      label: i18n.t('labels.pt'),
      value: 'pt'
    },
    {
      label: i18n.t('labels.en'),
      value: 'en'
    },
    {
      label: i18n.t('labels.es'),
      value: 'es'
    }
  ]))

  const options = useLocalStorage<any>('userOptions', {
    id: '',
    lang: 'pt',
    mode: 0,
    dark: 1,
    fullWidth: 1,
    volume: 100,
    autoplay: true,
    skipOrientation: false,
    assistantPosition: { x: null, y: null },
    lastUpdate: null,
    lastSyncTimestamp: null,
    forceSync: false
  }, {
    mergeDefaults: true
  })

  const status = ref(0)

  const nStatus = reactive({
    loadParams: 'initial' as LoaderType
  })

  const playing = reactive<ViewMediaType>({type: null, src: null, trackProgress: false, progress: 0, objData: null, accessDetail: false})
  const hideMenu = ref(false)

  const tempConfig = ref<typeof configModel>(safeCopy(configModel))
  const adminConfigLoaded = ref(false)
  const previewMode = ref(false)

  const getConfig = computed<typeof clientSettings.value.settings>(() => {
    if (batchMode.active) return batchMode.parameters as any

    return !previewMode.value ? clientSettings.value.settings : tempConfig.value
  })

  const forClient = computed(() => options.value.mode === 0)

  const loading = computed(() => status.value === 1)

  const tenantIdParameters = computed(() => tenantParameters.value)

  const termsOfUseStatus = ref<{
    id: string
    mode?: 'accept' | 'preview'
    show?: boolean
    content?: string
  }>({
    id: '',
    mode: 'accept',
    show: false,
    content: ''
  })

  const reset = (): void => Object.assign(tempConfig.value, safeCopy(clientSettings.value.settings))

  const save = async () => {
    const previousValues = safeCopy(clientSettings.value.settings) as typeof configModel

    // Cria / Atualiza as novas imagens do carrossel
    for (let i = 0; i < tempConfig.value.login.carousels.length; i++) {
      const { slides } = tempConfig.value.login.carousels[i]

      for (let j = 0; j < slides.length; j++) {
        const { file } = slides[j]

        if (file) {
          const { data, success } = await parametrosService.dataUrlUpload(file)

          if (success) {
            if (file.type.includes('image')) {
              tempConfig.value.login.carousels[i].slides[j].image = data
            } else if (file.type.includes('video')) {
              const ext = file.name.split('.').pop().toLowerCase()

              if ([ 'mp4', 'webm', 'avi', 'ogv' ].includes(ext)) {
                tempConfig.value.login.carousels[i].slides[j].video[ext] = data
                delete tempConfig.value.login.carousels[i].slides[j].video.url
              }
              else tempConfig.value.login.carousels[i].slides[j].video.url = data
            }

            delete tempConfig.value.login.carousels[i].slides[j].file
          }
        }
      }
    }

    // Cria / Atualiza as novas imagens de logos
    const uploadsStatus = [] as Promise<void>[]

    // Verifica a necessidade de upload de Banners
    if (tempConfig.value.banners.items?.length) {
      tempConfig.value.banners.items.forEach((b) => {
        // @ts-ignore O atributo file é temporário e não faz parte do objeto original
        const f = b?.file

        if (f) {
          uploadsStatus.push(paramsService.uploadFile(f).then(({ data: url }) => {
            b.id = url.split('/').pop().split('.')[0]
            b.url = url
            b.public = false

            // @ts-ignore
            delete b.file
          }))
        }
      })
    }

    // Verifica a necessidade de upload de Logos
    Object.keys(logoFiles).forEach((key) => {
      if (!logoFiles[key]) return

      uploadsStatus.push(paramsService.uploadFile(logoFiles[key])
        .then(({ data: url }) => {
          tempConfig.value.logos[key] = url
          logoFiles[key] = null
        }))
    })

    await Promise.all(uploadsStatus)

    const newValues = safeCopy(tempConfig.value) as typeof configModel

    // Cria / Atualiza as novas imagens de banners
    // for (let i = 0; i < newValues.banners.items.length; i++) {
    //   const { url, name } = newValues.banners.items[i]

    //   if (url.substring(0, 10) === 'data:image') {
    //     const { data, success } = await parametrosService.dataUrlUpload(url, name)
    //     if (success) newValues.banners.items[i].url = data
    //   }
    // }

    // Cria / Atualiza as novas imagens de fundo (antigo)
    for (let i = 0; i < newValues.login.backgrounds.length; i++) {
      const imgInfo = newValues.login.backgrounds[i]

      if (imgInfo?.[0]?.substring(0, 10) === 'data:image') {
        const { data, success } = await parametrosService.dataUrlUpload(imgInfo[0], imgInfo[1])
        if (success) newValues.login.backgrounds[i] = [data, data?.substring(data.lastIndexOf('/') + 1, data.length)]
      }
    }

    // const oldBanner = previousValues.banners.items.map((bannerUrl) => bannerUrl.url)
    // const newBanner = newValues.banners.items.map((bannerUrl) => bannerUrl.url)

    // // Percorre o array de imagens atuais, para ver quais não existem mais (devido a remoção/update)
    // oldBanner.map(async (bannerUrl) => {
    //   if (!newBanner.includes(bannerUrl))
    //     await parametrosService.deleteImg(bannerUrl.substring(bannerUrl.lastIndexOf('/') + 1, bannerUrl.length))
    // })

    const oldBGImgs = previousValues.login.backgrounds.map((bg) => bg[0])
    const newBGImgs = newValues.login.backgrounds.map((bg) => bg[0])

    // Percorre o array de imagens atuais, para ver quais não existem mais (devido a remoção/update)
    oldBGImgs.map(async (bg) => {
      if (!newBGImgs.includes(bg)) await parametrosService.deleteImg(bg?.substring(bg.lastIndexOf('/') + 1, bg.length))
    })

    // Imagens dos slides do carrossel
    const oldSlideImages = previousValues.login.carousels.map((carousel) => carousel.slides.map((slide) => slide.image))
    const newSlideImages = newValues.login.carousels.map((carousel) => carousel.slides.map((slide) => slide.image))

    oldSlideImages.map(async (carousel) => {
      carousel.map(async (img) => {
        if (!newSlideImages.flat().includes(img)) await parametrosService.deleteImg(img?.substring(img.lastIndexOf('/') + 1, img.length))
      })
    })

    // Videos dos slides do carrossel
    const oldSlideVideos = previousValues.login.carousels.map((carousel) => carousel.slides.map((slide) => slide.video && (slide.video.url || slide.video.mp4 || slide.video.webm || slide.video.ogv || slide.video.avi)))
    const newSlideVideos = newValues.login.carousels.map((carousel) => carousel.slides.map((slide) => slide.video && (slide.video.url || slide.video.mp4 || slide.video.webm || slide.video.ogv || slide.video.avi)))

    oldSlideVideos.map(async (carousel) => {
      carousel.map(async (video) => {
        if (!newSlideVideos.flat().includes(video)) await parametrosService.deleteImg(video?.substring(video.lastIndexOf('/') + 1, video.length))
      })
    })

    // Verifica se houve alteração dos termos de uso
    const updatedTerms = newValues.general.termOfUse !== previousValues.general.termOfUse

    if(newValues.theme.defaultTheme !== 'DefinidoPeloUsuario') newValues.theme.initialTheme = null


    Object.assign(tempConfig.value, safeCopy(newValues))
    Object.assign(clientSettings.value.settings, safeCopy(tempConfig.value))

    newValues.login.backgrounds = newBGImgs as unknown as string[][] // O endpoint aguarda array de strings apenas

    if (newValues.theme) {
      //@ts-ignore
      newValues.tema = {
        temaPadrao: newValues.theme.defaultTheme,
        temaDeInicializacao: newValues.theme.initialTheme
      }

      delete newValues.theme
    }

    // Não precisa propriedades padrões do sistema
    delete newValues.defaultScripts

    // Não podemos enviar SSOConfig para o endpoint de atualização
    // @ts-ignore
    delete newValues.ssoConfig

    // Arrumando a bagunça
    status.value = 1

    try {
      parametrosService.silent(true)
      const { success } = await parametrosService.update({
        ...newValues
      })

      if (success) {
        toast.success({
          title: i18n.t('feedback.success'),
          message: i18n.t('toasts.system.saveSuccess')
        })

        if (updatedTerms) {
          useAuthStore().resetUserTermsOfUse()

          if (newValues.general.termOfUse) {
            checkUserTermsAgreement()
          }
        }
      }
      status.value = 2
    } catch (error) {
      status.value = -1
    }
  }

  const loadParams = async (asAdmin?: boolean) => {
    if (nStatus.loadParams === 'loading')
      return

    const host = window.location.host
    const isPlantar = host.includes('plantareducacao.com.br')
    nStatus.loadParams = 'loading'

    try {
      useProgress().start()
      clientSettings.value.status = 'loading'
      const publicRoute = asAdmin || !useAuthStore().loggedIn

      const { success, data } = await (!publicRoute
        ? parametrosService.get()
        : parametrosService.clientSettings({
            subDomain: isPlantar ? host.split('.')[0] : ' ',
            clientUrl: isPlantar ? ' ' : host
          })
        )

      if (success) {
        if (data && data?.tenantId) tenantParameters.value = data.tenantId

        // Normaliza o resultado
        let remoteSettings = safeJson(data?.jsonConfigParams) || data || {}
        if (publicRoute) remoteSettings = ObjKeysLcFirst(remoteSettings)

        // Limpa recursivamente os atributos que são vazios (desse endpoint, ex: data.colors = null)
        const cleanDeep = (obj) => {
          if (obj === null || !isObject(obj)) return
          Object.keys(obj).forEach((key) => {
            if (obj[key] && typeof obj[key] === 'object') cleanDeep(obj[key])
            else if (obj[key] === null) delete obj[key]
          })
        }

        cleanDeep(remoteSettings)

        if (remoteSettings?.tema) {
          remoteSettings.theme = {
            defaultTheme: remoteSettings.tema.temaPadrao,
            initialTheme: remoteSettings.tema.temaDeInicializacao ?? null
          }

          delete remoteSettings.tema
        }

        if (remoteSettings?.botaoSso) {
          remoteSettings.ssoButton = {
            enabled: remoteSettings.botaoSso?.habilitarBotaoPersonalizadoSso || false,
            label: remoteSettings.botaoSso?.textoBotaoSso || '',
            linkImage: remoteSettings.botaoSso?.linkImagemBotaoSso || '',
          }

          delete remoteSettings.botaoSso
        }


        const mergedSettings = mergeDeep(safeCopy(configModel), remoteSettings)

        if (mergedSettings.login?.backgrounds.length) {
          mergedSettings.login.backgrounds = mergedSettings.login.backgrounds.map((bg) => {
            return typeof bg === 'string' ? [bg, bg.split('/').pop()] : bg
          })
        }

        Object.assign(clientSettings.value.settings, safeCopy(mergedSettings))
        Object.assign(tempConfig.value, safeCopy(mergedSettings))

        clientSettings.value.aad = {
          enabled: data?.ssoConfig?.enableSso || false,
          provider: data?.ssoConfig?.provider || '',
          origin: data?.ssoConfig?.origin || '',
        }

        adminConfigLoaded.value = true
        nStatus.loadParams = 'success'
      }
    } catch (error) {
      nStatus.loadParams = 'error'
      clientSettings.value.status = 'error'
      throw new Error(error)
    } finally {
      clientSettings.value.status = 'complete'
      clientSettings.value.loadedOnce = true

      useProgress().done()
    }
  }

  const resetConfig = () => {
    Object.assign(tempConfig.value, safeCopy(configModel))
    Object.assign(clientSettings.value.settings, safeCopy(configModel))
  }

  const setAdminMode = () => (options.value.mode = 1)
  const setClientMode = () => (options.value.mode = 0)
  const toggleTheme = () => (options.value.dark = 1 - options.value.dark)
  const toggleWidth = () => (options.value.fullWidth = 1 - options.value.fullWidth)
  const setVolume = (value: number) => (options.value.volume = value)
  const setTheme = (value: number) => (options.value.dark = value)
  const play = (props: ViewMediaType) => {
    stopPlaying()
    nextTick(() => {
      Object.assign(playing, { ...props })
    })
  }

  const stopPlaying = () => Object.assign(playing, {
    id: null,
    type: null,
    src: null,
    trackProgress: false,
    progress: 0,
    title: null,
    objData: null,
    accessDetail: false,
    ignoreFilterSection: false,
    slides: [],
    mediaId: null
  })

  const isDark = computed(
    () =>
      options.value.dark === 1 ||
      (options.value.dark === 2 && window.matchMedia('(prefers-color-scheme: dark)').matches)
  )

  const setLanguage = (lang?: string) => {
    lang = lang || options.value.lang || clientSettings.value.lang

    clientSettings.value.lang = lang
    options.value.lang = lang
    i18n.locale.value = lang
  }

  const checkUserTermsAgreement = () => {
    const { contract: inContract } = webStorage.getItem('client-mode') || {}

    if (useAuthStore().mustAcceptTermsOfUse && !inContract) {
      termsOfUseStatus.value = {
        id: getConfig.value.general.termOfUse,
        mode: 'accept',
        show: true
      }
    }
  }

  // Condicional para abrir a janela de termos de uso automáticamente
  watch(
    () => useAuthStore().mustAcceptTermsOfUse,
    (value) => {
      if (value) checkUserTermsAgreement()
    }, { immediate: true }
  )

  const loadTermsOfUse = async (id?: string) => {
    return termsService.getById(id || termsOfUseStatus.value.id)
      .then(({ data, success }) => {
        if (!success) return

        termsOfUseStatus.value.content = data.conteudo
      })
      .catch((error) => {
        throw new Error(error)
      })
  }

  return {
    availableLangs: langs,
    nStatus,
    clientSettings,
    tempConfig,
    previewMode,
    options,
    playing,
    hideMenu,
    adminConfigLoaded,
    viewTermsOfUse: readonly(termsOfUseStatus),
    // Getters
    getConfig,
    forClient,
    isDark,
    loading,
    tenantIdParameters,
    // Actions
    reset,
    save,
    loadParams,
    loadTermsOfUse,
    resetConfig,
    setAdminMode,
    setLogo: (type: keyof typeof logoFiles, file: File) => {
      logoFiles[type] = file
    },
    setClientMode,
    setLanguage,
    toggleTheme,
    setTheme,
    toggleWidth,
    setVolume,
    play,
    stopPlaying,
    openTermsOfUse: (id?: string, mode?: 'accept' | 'preview', content?: string) => {
      const newId = id || getConfig.value.general.termOfUse

      if (content) {
        termsOfUseStatus.value.content = content
      } else if (termsOfUseStatus.value.id !== newId) {
        termsOfUseStatus.value.content = ''
      }

      termsOfUseStatus.value.id = newId

      termsOfUseStatus.value.mode = mode || 'preview'
      termsOfUseStatus.value.show = true
    },
    closeTermsOfUse: () => (termsOfUseStatus.value.show = false),
    checkUserTermsAgreement,
    setBgsAndBanners: (banners: any[]) => {
      clientSettings.value.settingsBackgroundBanner = banners
    }
  }
})

export default useSystemStore
